/**
 * Outil « Gram-Schmidt et projection » (Boîte à outils, prépa).
 *
 * Orthonormaliser : une famille de ℝ³ (2 ou 3 vecteurs à coordonnées
 * entières) qu'on orthonormalise étape par étape — on retire à e_k ses
 * projections sur les ε déjà construits (projeté en pointillés, angle droit
 * marqué, plan Vect(ε₁, ε₂) pour e₃), puis on normalise. Calculs exacts.
 * Projeter : projeté orthogonal de x sur une droite ou un plan engendré par
 * la famille, distance d(x, F) et Pythagore ; coordonnées de x dans la base
 * orthonormée obtenue. Défi : normaliser, orthogonaliser, projeter, lire une
 * coordonnée, une distance. Logique : `gram.ts`.
 */

import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { cn } from '@jeraisonne/ui';
import { Note, PRIMARY_BUTTON, SECONDARY_BUTTON, Segmented } from '../../ui/controls';
import { frenchSpaces } from '../../ui/format';
import { seededRng } from '../../ui/random';
import { FIGURE_BG_CLASS, HaloText, INK, MUTED, VectorArrow } from '../../ui/svg-parts';
import { ORBIT_FOCUS_CLASS, OrbitButtons, pointsAttr, projector, useOrbit } from '../../ui/view3d';
import { add, norm, scale, sub, type Camera, type V3 } from '../../ui/space3d';
import { mul, frac as mkFrac, type Frac } from '../../ui/exact';
import {
  COORD_MAX,
  COORD_MIN,
  PRESETS,
  QUIZ_KINDS,
  fracText,
  gramSchmidt,
  idx,
  makeQuiz,
  project,
  qnum,
  qzero,
  rootOf,
  stageLines,
  stageShort,
  stagesOf,
  surdText,
  unitText,
  vecText,
  type IV,
  type Quiz,
  type Stage,
  type Surd,
} from './gram';

const SIZE = 320;
const COLORS = ['var(--color-primary-500)', 'var(--color-accent-600)', 'var(--color-success-600)'];
const PROJ = 'var(--color-success-600)';
const SELECT =
  'border-ink-200 text-ink-900 min-h-11 rounded-xl border bg-white px-1 text-center text-[15px] tabular-nums';
const VALUES = Array.from({ length: COORD_MAX - COORD_MIN + 1 }, (_, i) => COORD_MIN + i);
const unit = (v: V3): V3 => {
  const n = norm(v);
  return n < 1e-12 ? [0, 0, 0] : scale(v, 1 / n);
};
const num = (w: IV): V3 => [w[0], w[1], w[2]];

type Fam = [IV, IV, IV];

/** « p_F(x) » dans la figure : F en indice (tspan) */
function SubText({ text }: { text: string }) {
  const m = /^(.*?)_([A-Z])(.*)$/.exec(text);
  if (!m) return <>{text}</>;
  return (
    <>
      {m[1]}
      <tspan baselineShift="sub" fontSize={12}>
        {m[2]}
      </tspan>
      {m[3]}
    </>
  );
}

/** texte de l'outil : « p_F(x) », « p_D », « p_P » écrits avec l'indice ; espaces françaises */
function Sub({ text }: { text: string }) {
  return (
    <>
      {frenchSpaces(text)
        .split(/(p_[A-Z])/)
        .map((part, i) =>
          /^p_[A-Z]$/.test(part) ? (
            <span key={i}>
              p<sub>{part.slice(2)}</sub>
            </span>
          ) : (
            part
          ),
        )}
    </>
  );
}

/* ---------- figure 3D ---------- */

interface Arrow {
  from?: V3;
  to: V3;
  color: string;
  width?: number;
  dashed?: boolean;
  opacity?: number;
  /** « e₁ », ou « p_F(x) » (l'indice est dessiné en indice) */
  label?: string;
}

interface SceneProps {
  cam: Camera;
  /** rayon de la scène (unités) */
  R: number;
  arrows: Arrow[];
  /** plan Vect(a, b) (a, b orthonormés) ou droite Vect(a) */
  plane?: [V3, V3];
  line?: V3;
  /** angle droit au point P entre les directions a et b */
  right?: { at: V3; a: V3; b: V3 };
  /** segment en pointillés (ex. de p_F(x) à x) */
  dash?: [V3, V3];
}

/**
 * Étiquettes au bout des flèches, sans chevauchement : les dernières flèches
 * (ε, u : le résultat de l'étape) gardent leur place, les autres s'écartent
 * perpendiculairement à leur flèche.
 */
function placeLabels(arrows: readonly Arrow[], P: (p: V3) => [number, number]) {
  const placed: { key: string; x: number; y: number; text: string; color: string }[] = [];
  for (let i = arrows.length - 1; i >= 0; i--) {
    const a = arrows[i]!;
    if (!a.label) continue;
    const [x1, y1] = P(a.from ?? [0, 0, 0]);
    const [x2, y2] = P(a.to);
    const d = Math.hypot(x2 - x1, y2 - y1);
    // un vecteur nul n'a ni flèche ni étiquette
    if (norm(sub(a.to, a.from ?? [0, 0, 0])) < 1e-9 || d < 1) continue;
    const ux = (x2 - x1) / d;
    const uy = (y2 - y1) / d;
    const at = (k: number) => ({
      x: Math.min(SIZE - 16, Math.max(16, x2 + ux * 13 - uy * k)),
      y: Math.min(SIZE - 6, Math.max(14, y2 + uy * 13 + ux * k + 4)),
    });
    const free = (q: { x: number; y: number }) =>
      placed.every((o) => Math.abs(o.x - q.x) > 22 || Math.abs(o.y - q.y) > 15);
    const spot = [0, 16, -16, 30, -30].map(at).find(free) ?? at(0);
    placed.push({ key: `l${i}`, ...spot, text: a.label, color: a.color });
  }
  return placed;
}

function Scene({ cam, R, arrows, plane, line, right, dash }: SceneProps) {
  const s = (SIZE / 2 - 26) / (R * 1.15);
  const P = projector(cam, [0, 0, 0], s, SIZE);
  const L = R * 1.1;
  const axes: [V3, string][] = [
    [[1, 0, 0], 'x'],
    [[0, 1, 0], 'y'],
    [[0, 0, 1], 'z'],
  ];
  return (
    <>
      {axes.map(([a, name]) => {
        const [x1, y1] = P(scale(a, -L));
        const [x2, y2] = P(scale(a, L));
        const [lx, ly] = P(scale(a, L * 1.07));
        return (
          <g key={name}>
            <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={MUTED} strokeWidth={1} />
            <text x={lx} y={ly + 4} fontSize={12} fill="var(--color-ink-500)" textAnchor="middle">
              {name}
            </text>
          </g>
        );
      })}
      {plane && (
        <polygon
          points={pointsAttr(
            [
              add(scale(plane[0], L), scale(plane[1], L)),
              add(scale(plane[0], -L), scale(plane[1], L)),
              add(scale(plane[0], -L), scale(plane[1], -L)),
              add(scale(plane[0], L), scale(plane[1], -L)),
            ].map(P),
          )}
          fill="var(--color-primary-500)"
          fillOpacity={0.1}
          stroke="var(--color-primary-500)"
          strokeOpacity={0.45}
          strokeWidth={1}
        />
      )}
      {line &&
        (() => {
          const [x1, y1] = P(scale(line, -L));
          const [x2, y2] = P(scale(line, L));
          return (
            <line
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke="var(--color-primary-500)"
              strokeOpacity={0.55}
              strokeWidth={2}
              strokeDasharray="8 5"
            />
          );
        })()}
      {dash &&
        (() => {
          const [x1, y1] = P(dash[0]);
          const [x2, y2] = P(dash[1]);
          return (
            <line
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke={INK}
              strokeWidth={1.5}
              strokeDasharray="5 4"
            />
          );
        })()}
      {arrows.map((a, i) => {
        const [x1, y1] = P(a.from ?? [0, 0, 0]);
        const [x2, y2] = P(a.to);
        return (
          <g key={i} opacity={a.opacity ?? 1}>
            <VectorArrow
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              color={a.color}
              width={a.width ?? 3}
              dashed={a.dashed}
            />
          </g>
        );
      })}
      {/* l'angle droit par-dessus les flèches : la pointe d'un projeté court ne le cache pas */}
      {right &&
        (() => {
          const k = R * 0.09;
          const pts = [
            add(right.at, scale(right.a, k)),
            add(add(right.at, scale(right.a, k)), scale(right.b, k)),
            add(right.at, scale(right.b, k)),
          ].map(P);
          return (
            <path
              d={`M ${pts[0]![0]} ${pts[0]![1]} L ${pts[1]![0]} ${pts[1]![1]} L ${pts[2]![0]} ${pts[2]![1]}`}
              fill="none"
              stroke={INK}
              strokeWidth={1.5}
            />
          );
        })()}
      {placeLabels(arrows, P).map((l) => (
        <HaloText
          key={l.key}
          x={l.x}
          y={l.y}
          fontSize={14}
          fontWeight={700}
          fill={l.color}
          textAnchor="middle"
        >
          <SubText text={l.text} />
        </HaloText>
      ))}
    </>
  );
}

function Figure({
  label,
  children,
  orbit,
}: {
  label: string;
  children: ReactNode;
  orbit: ReturnType<typeof useOrbit>;
}) {
  return (
    <div className="space-y-2">
      <svg
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        role="img"
        aria-label={frenchSpaces(label)}
        className={cn(
          'border-ink-100 mx-auto block w-full max-w-[360px] cursor-grab rounded-2xl border',
          FIGURE_BG_CLASS,
          ORBIT_FOCUS_CLASS,
        )}
        {...orbit.svgProps}
      >
        {children}
      </svg>
      <OrbitButtons orbit={orbit} />
    </div>
  );
}

/* ---------- saisie ---------- */

function VecRow({ name, value, onChange }: { name: string; value: IV; onChange: (v: IV) => void }) {
  const coords = ['x', 'y', 'z'];
  return (
    <div role="group" aria-label={name} className="flex items-center gap-1.5">
      <span className="text-ink-900 w-12 shrink-0 text-base font-bold">{name} =</span>
      <span aria-hidden className="text-ink-500 text-lg">
        (
      </span>
      {value.map((c, i) => (
        <span key={i} className="flex items-center gap-1.5">
          <select
            className={cn(SELECT, 'w-14')}
            aria-label={`${name} : coordonnée ${coords[i]}`}
            value={c}
            onChange={(e) => {
              const v = [...value] as [number, number, number];
              v[i] = Number(e.target.value);
              onChange(v);
            }}
          >
            {VALUES.map((n) => (
              <option key={n} value={n}>
                {String(n).replace('-', '−')}
              </option>
            ))}
          </select>
          {i < 2 && (
            <span aria-hidden className="text-ink-500">
              ;
            </span>
          )}
        </span>
      ))}
      <span aria-hidden className="text-ink-500 text-lg">
        )
      </span>
    </div>
  );
}

function FamilyEditor({
  fam,
  n,
  onFam,
  onN,
}: {
  fam: Fam;
  n: 2 | 3;
  /** `preset` : famille toute prête (on repart de la première étape) */
  onFam: (f: Fam, preset?: boolean) => void;
  onN: (n: 2 | 3) => void;
}) {
  const id = useId();
  // « Famille liée » n'a de sens qu'à 3 vecteurs
  const preset = PRESETS.findIndex(
    (p) =>
      !(p.n === 3 && n === 2) &&
      p.fam.slice(0, n).every((v, i) => v.every((c, j) => c === fam[i]![j])),
  );
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-end gap-2">
        <Segmented
          ariaLabel="Taille de la famille"
          value={String(n) as '2' | '3'}
          options={[
            { value: '2', label: '2 vecteurs' },
            { value: '3', label: '3 vecteurs' },
          ]}
          onChange={(v) => onN(v === '2' ? 2 : 3)}
        />
        <label htmlFor={id} className="min-w-[10rem] flex-1">
          <span className="text-ink-500 block text-xs">Famille toute prête</span>
          <select
            id={id}
            className={cn(SELECT, 'w-full px-2 text-left')}
            value={preset >= 0 ? String(preset) : ''}
            onChange={(e) => {
              const p = PRESETS[Number(e.target.value)];
              if (e.target.value !== '' && p) {
                onFam(p.fam.map((v) => [...v] as const) as Fam, true);
                if (p.n) onN(p.n);
              }
            }}
          >
            {preset < 0 && <option value="">Famille personnalisée</option>}
            {PRESETS.map((p, i) => (
              <option key={p.label} value={i}>
                {p.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      {fam.slice(0, n).map((v, i) => (
        <VecRow
          key={i}
          name={`e${idx(i + 1)}`}
          value={v}
          onChange={(nv) => {
            const f = [...fam] as Fam;
            f[i] = nv;
            onFam(f);
          }}
        />
      ))}
    </div>
  );
}

/* ---------- orthonormaliser ---------- */

function sceneRadius(vs: readonly V3[]) {
  return Math.max(1.2, ...vs.map(norm));
}

function GSPanel({ fam, n }: { fam: Fam; n: 2 | 3 }) {
  const orbit = useOrbit({ yaw: 0.45, pitch: 0.38 });
  const [stage, setStage] = useState(0);
  const steps = useMemo(() => gramSchmidt(fam.slice(0, n)), [fam, n]);
  const stages = stagesOf(steps, n);
  const si = Math.min(stage, stages.length - 1);
  const st: Stage = stages[si]!;
  const lines = stageLines(steps, st, n);
  const short = stageShort(steps, st);
  const done = (k: number) => stages.slice(0, si + 1).some((x) => x.kind === 'norm' && x.k === k);

  const es = fam.slice(0, n).map(num);
  const eps = steps.map((s) => unit(num(s.w)));
  const R = sceneRadius(es);
  const arrows: Arrow[] = es.map((e, i) => ({
    to: e,
    color: COLORS[i]!,
    width: 2,
    opacity: done(i + 1) ? 0.4 : 0.85,
    label: `e${idx(i + 1)}`,
  }));
  let plane: [V3, V3] | undefined;
  let line: V3 | undefined;
  let right: SceneProps['right'];
  let dash: [V3, V3] | undefined;
  if (st.kind !== 'start') {
    const s = steps[st.k - 1]!;
    const e = es[st.k - 1]!;
    if (st.kind === 'orth') {
      const p = qnum(s.proj);
      const u = qnum(s.u);
      if (st.k === 2) line = eps[0];
      else plane = [eps[0]!, eps[1]!];
      if (norm(p) > 1e-9) arrows.push({ to: p, color: INK, width: 2, dashed: true });
      if (s.free) {
        arrows.push({ to: u, color: COLORS[st.k - 1]!, width: 3, label: `u${idx(st.k)}` });
        dash = [p, e];
        right = { at: p, a: unit(sub(e, p)), b: norm(p) > 1e-9 ? unit(p) : eps[0]! };
      }
    } else if (st.k > 1 && s.free) {
      arrows.push({ to: qnum(s.u), color: COLORS[st.k - 1]!, width: 2, dashed: true });
    }
  }
  for (let k = 1; k <= steps.length; k++)
    if (done(k))
      arrows.push({ to: eps[k - 1]!, color: COLORS[k - 1]!, width: 4, label: `ε${idx(k)}` });
  const built = steps.filter((s) => done(s.k) && !(st.kind === 'norm' && st.k === s.k));

  return (
    <div className="space-y-3">
      <Figure orbit={orbit} label={`Étape ${si + 1} sur ${stages.length}. ${short}`}>
        <Scene
          cam={orbit.cam}
          R={R}
          arrows={arrows}
          plane={plane}
          line={line}
          right={right}
          dash={dash}
        />
      </Figure>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          className={cn(SECONDARY_BUTTON, 'disabled:opacity-40')}
          onClick={() => setStage(Math.max(0, si - 1))}
          disabled={si === 0}
        >
          Étape précédente
        </button>
        {si < stages.length - 1 ? (
          <button type="button" className={PRIMARY_BUTTON} onClick={() => setStage(si + 1)}>
            Étape suivante
          </button>
        ) : (
          <button type="button" className={PRIMARY_BUTTON} onClick={() => setStage(0)}>
            Recommencer
          </button>
        )}
        <span className="text-ink-500 text-sm tabular-nums">
          {frenchSpaces(`Étape ${si + 1} / ${stages.length}`)}
        </span>
      </div>
      <p className="sr-only" aria-live="polite">
        {frenchSpaces(`Étape ${si + 1} sur ${stages.length}. ${short}`)}
      </p>
      <Note live={false}>
        {lines.map((l) => (
          <span key={l} data-testid="gs-line" className="mb-1 block last:mb-0">
            {frenchSpaces(l)}
          </span>
        ))}
      </Note>
      {built.length > 0 && (
        <p className="text-ink-700 text-sm" data-testid="gs-built">
          {frenchSpaces(
            `Déjà construits : ${built.map((s) => `ε${idx(s.k)} = ${unitText(s.w)}`).join(', ')}.`,
          )}
        </p>
      )}
      {si === stages.length - 1 && steps.length === n && steps.every((s) => s.free) && (
        <Note live={false} tone="success">
          {frenchSpaces(
            `(${steps.map((s) => `ε${idx(s.k)}`).join(', ')}) est orthonormée et engendre le même espace que (${steps.map((s) => `e${idx(s.k)}`).join(', ')}).`,
          )}
        </Note>
      )}
    </div>
  );
}

/* ---------- projeter ---------- */

const sq = (s: Surd): Frac => mul(mul(s.c, s.c), mkFrac(s.r));
const paren = (t: string) => (/[−/]/.test(t) ? `(${t})` : t);

function ProjPanel({ fam, n }: { fam: Fam; n: 2 | 3 }) {
  const orbit = useOrbit({ yaw: 0.45, pitch: 0.38 });
  const [dim, setDim] = useState<'1' | '2'>('2');
  const [x, setX] = useState<IV>([1, 2, 3]);
  const gens = useMemo(() => fam.slice(0, Number(dim)), [fam, dim]);
  const P = useMemo(() => project(x, gens), [x, gens]);
  const all = useMemo(() => (n === 3 ? project(x, fam) : null), [x, fam, n]);
  const X = num(x);
  const p = qnum(P.p);
  const eps = P.basis.map((b) => unit(num(b.w)));
  const R = sceneRadius([X, ...gens.map(num)]);
  const arrows: Arrow[] = eps.map((e, i) => ({
    to: e,
    color: COLORS[i]!,
    width: 4,
    label: `ε${idx(i + 1)}`,
  }));
  arrows.push({ to: X, color: INK, width: 3, label: 'x' });
  if (norm(p) > 1e-9) arrows.push({ to: p, color: PROJ, width: 3, label: 'p_F(x)' });
  const inF = qzero(P.r);
  const right =
    !inF && eps.length > 0
      ? { at: p, a: unit(sub(X, p)), b: norm(p) > 1e-9 ? unit(p) : eps[0]! }
      : undefined;

  const F = dim === '1' ? 'Vect(e₁)' : 'Vect(e₁, e₂)';
  const lines: string[] = [];
  const isNull = (v: IV) => v.every((c) => c === 0);
  if (P.basis.length === 0)
    lines.push(
      gens.length === 1
        ? 'e₁ est nul : F = {0}, et p_F(x) = 0.'
        : 'e₁ et e₂ sont nuls : F = {0}, et p_F(x) = 0.',
    );
  else {
    // le générateur écarté par le procédé : nul, ou colinéaire à l'autre
    if (P.basis.length < gens.length) {
      const dropped = gens.findIndex((_, i) => !P.kept.includes(i));
      lines.push(
        dropped === 0
          ? 'e₁ est nul : F = Vect(e₂), la droite dirigée par e₂.'
          : isNull(gens[1]!)
            ? 'e₂ est nul : F = Vect(e₁), la droite dirigée par e₁.'
            : 'e₂ est colinéaire à e₁ : F est la droite Vect(e₁).',
      );
    }
    lines.push(
      `Base orthonormée de F : ${P.basis.map((b, i) => `ε${idx(i + 1)} = ${unitText(b.w)}`).join(', ')}.`,
    );
    const terms = P.basis.map((_, i) => `⟨x, ε${idx(i + 1)}⟩ ε${idx(i + 1)}`).join(' + ');
    const vals = P.coords.map((c, i) => `⟨x, ε${idx(i + 1)}⟩ = ${surdText(c)}`).join(' et ');
    lines.push(`p_F(x) = ${terms}, avec ${vals}.`);
    lines.push(`p_F(x) = ${vecText(P.p)}`);
  }
  if (inF) lines.push('x − p_F(x) = 0 : x est dans F, sa distance à F est nulle.');
  else {
    lines.push(`x − p_F(x) = ${vecText(P.r)}`);
    const root = rootOf(P.dist2);
    lines.push(
      `d(x, F) = ‖x − p_F(x)‖ = ${root}${root === surdText(P.dist) ? '' : ` = ${surdText(P.dist)}`}`,
    );
  }
  const x2 = x[0] * x[0] + x[1] * x[1] + x[2] * x[2];
  lines.push(
    `Pythagore : ‖x‖² = ‖p_F(x)‖² + d(x, F)², soit ${x2} = ${fracText(P.p2)} + ${fracText(P.dist2)}.`,
  );

  const coordLines: string[] = [];
  if (all && all.basis.length === 3) {
    const names = all.basis.map((_, i) => `ε${idx(i + 1)}`);
    const cs = all.coords.map(surdText);
    // termes nuls omis : « x = 2√3/3 ε₃ »
    const terms = cs.flatMap((c, i) => (c === '0' ? [] : [`${paren(c)} ${names[i]}`]));
    coordLines.push(
      `Dans la base orthonormée (${names.join(', ')}) : x = ${terms.join(' + ') || '0'}.`,
    );
    coordLines.push(`Coordonnées : ${names.map((e, i) => `⟨x, ${e}⟩ = ${cs[i]}`).join(', ')}.`);
    coordLines.push(
      `‖x‖² = ${cs.map((c) => `${paren(c)}²`).join(' + ')} = ${all.coords.map((c) => fracText(sq(c))).join(' + ')} = ${x2}.`,
    );
  }
  const short = inF
    ? `x = ${vecText(x)} est dans F.`
    : `Projeté de x = ${vecText(x)} sur F = ${F} : p_F(x) = ${vecText(P.p)}, distance ${surdText(P.dist)}.`;

  return (
    <div className="space-y-3">
      <Segmented
        ariaLabel="Sous-espace F"
        value={dim}
        options={[
          { value: '1', label: 'Droite Vect(e₁)' },
          { value: '2', label: 'Plan Vect(e₁, e₂)' },
        ]}
        onChange={setDim}
      />
      <VecRow name="x" value={x} onChange={setX} />
      <Figure orbit={orbit} label={short}>
        <Scene
          cam={orbit.cam}
          R={R}
          arrows={arrows}
          plane={eps.length === 2 ? [eps[0]!, eps[1]!] : undefined}
          line={eps.length === 1 ? eps[0] : undefined}
          right={right}
          dash={inF ? undefined : [p, X]}
        />
      </Figure>
      <p className="sr-only" aria-live="polite">
        {frenchSpaces(short)}
      </p>
      <Note live={false}>
        {lines.map((l) => (
          <span key={l} data-testid="proj-line" className="mb-1 block last:mb-0">
            <Sub text={l} />
          </span>
        ))}
      </Note>
      {coordLines.length > 0 && (
        <Note live={false}>
          {coordLines.map((l) => (
            <span key={l} data-testid="coord-line" className="mb-1 block last:mb-0">
              {frenchSpaces(l)}
            </span>
          ))}
        </Note>
      )}
      <p className="text-ink-500 text-xs">
        <Sub text="F est engendré par la famille de l’onglet « Orthonormaliser ». En pointillés : x − p_F(x), orthogonal à F ; sa longueur est la distance d(x, F)." />
      </p>
    </div>
  );
}

/* ---------- défi ---------- */

function Challenge({ seed }: { seed?: number }) {
  const rnd = useMemo(() => (seed === undefined ? Math.random : seededRng(seed)), [seed]);
  const rounds = QUIZ_KINDS.length;
  const [count, setCount] = useState(1);
  const [q, setQ] = useState<Quiz>(() => makeQuiz(QUIZ_KINDS[0]!, rnd));
  const [picked, setPicked] = useState<number | null>(null);
  const [score, setScore] = useState(0);
  const titleRef = useRef<HTMLParagraphElement>(null);
  const moved = useRef(false);
  const done = picked !== null && count >= rounds;
  const chosen = picked === null ? null : q.options[picked]!;

  useEffect(() => {
    if (moved.current) titleRef.current?.focus();
    moved.current = false;
  }, [q]);

  const choose = (i: number) => {
    if (picked !== null) return;
    setPicked(i);
    if (q.options[i]!.correct) setScore(score + 1);
  };
  const next = () => {
    const c = done ? 1 : count + 1;
    if (done) setScore(0);
    setCount(c);
    moved.current = true;
    setQ(makeQuiz(QUIZ_KINDS[c - 1]!, rnd));
    setPicked(null);
  };
  const right = q.options.find((o) => o.correct)!;

  return (
    <div className="space-y-3">
      <p className="text-ink-500 text-xs font-semibold uppercase tracking-wide">
        {frenchSpaces(`Question ${count} / ${rounds} · ${score} juste${score > 1 ? 's' : ''}`)}
      </p>
      <p ref={titleRef} tabIndex={-1} className="text-ink-900 text-base font-semibold outline-none">
        <Sub text={q.prompt} />
      </p>
      <div role="group" aria-label="Réponses" className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {q.options.map((o, i) => (
          <button
            key={o.text}
            type="button"
            onClick={() => choose(i)}
            aria-pressed={picked === i}
            aria-disabled={picked !== null && picked !== i}
            className={cn(
              'min-h-11 rounded-xl border px-3 py-2 text-center text-base font-semibold tabular-nums transition-colors',
              picked === null && 'border-ink-200 text-ink-800 hover:bg-ink-50',
              picked !== null && o.correct && 'border-success-600 bg-success-50 text-ink-900',
              picked === i && !o.correct && 'border-error-500 bg-error-50 text-ink-900',
              picked !== null && picked !== i && !o.correct && 'border-ink-100 text-ink-500',
            )}
          >
            <Sub text={o.text} />
          </button>
        ))}
      </div>
      <div aria-live="polite">
        {chosen && (
          <Note live={false} tone={chosen.correct ? 'success' : 'error'}>
            <span data-testid="gs-feedback">
              <Sub
                text={
                  chosen.correct
                    ? `Exact. ${q.explain}`
                    : `${chosen.hint ?? ''} La bonne réponse : ${right.text}. ${q.explain}`
                }
              />
            </span>
          </Note>
        )}
      </div>
      {picked !== null && (
        <div className="flex flex-wrap items-center gap-3">
          {done && (
            <p className="text-ink-900 text-sm font-semibold">
              {frenchSpaces(`Score : ${score} / ${rounds}`)}
            </p>
          )}
          <button type="button" className={done ? SECONDARY_BUTTON : PRIMARY_BUTTON} onClick={next}>
            {done ? 'Recommencer' : 'Question suivante'}
          </button>
        </div>
      )}
    </div>
  );
}

/* ---------- outil ---------- */

export function GramSchmidtTool({ seed }: { /** graine du défi (tests) */ seed?: number }) {
  const [mode, setMode] = useState<'gs' | 'proj' | 'quiz'>('gs');
  const [fam, setFam] = useState<Fam>(() => PRESETS[0]!.fam.map((v) => [...v] as const) as Fam);
  const [n, setN] = useState<2 | 3>(3);
  // une famille toute prête repart de l'étape 1 (clé du panneau)
  const [run, setRun] = useState(0);
  const changeFam = (f: Fam, preset = false) => {
    setFam(f);
    if (preset) setRun(run + 1);
  };
  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Ce qu’on fait"
        value={mode}
        options={[
          { value: 'gs', label: 'Orthonormaliser' },
          { value: 'proj', label: 'Projeter' },
          { value: 'quiz', label: 'Défi' },
        ]}
        onChange={setMode}
      />
      {mode !== 'quiz' && <FamilyEditor fam={fam} n={n} onFam={changeFam} onN={setN} />}
      <div hidden={mode !== 'gs'}>
        <GSPanel key={run} fam={fam} n={n} />
      </div>
      <div hidden={mode !== 'proj'}>
        <ProjPanel fam={fam} n={n} />
      </div>
      <div hidden={mode !== 'quiz'}>
        <Challenge seed={seed} />
      </div>
    </div>
  );
}
